// pages/SAPProcessMapOverview.jsx — one-page overview of the whole SAP process for a landscape:
// master data on top, the business cycles (buy, make, sell, maintain, services) in the middle,
// inventory as the hub that links them, and FI/CO at the bottom where every step ends up.
// Cycles and steps come from the same landscape data as the detailed flows, so the overview
// stays in line with them; clicking a cycle opens its detailed flow.
import { MODULES } from './sapProcessMapData';

const T = {
  title: { en: 'End-to-end overview', id: 'Gambaran menyeluruh' },
  sub: {
    en: 'How the SAP modules work together in this landscape. Click a cycle to open its detailed flow.',
    id: 'Bagaimana modul SAP saling terhubung di landscape ini. Klik satu siklus untuk membuka alur detailnya.',
  },
  md: { en: 'Master data', id: 'Master data' },
  mdNote: { en: 'Maintained mostly by the core team on request (Solman ticket / upload template)', id: 'Sebagian besar dipelihara core team atas permintaan (tiket Solman / template upload)' },
  cycles: { en: 'Business cycles', id: 'Siklus bisnis' },
  stock: { en: 'Inventory (MM) — stock quantity and value, valued with the Material Ledger', id: 'Persediaan (MM) — kuantitas dan nilai stok, dinilai dengan Material Ledger' },
  noStock: { en: 'No own inventory — goods are bought and sold third-party (shipped directly from supplier to customer)', id: 'Tidak ada persediaan sendiri — barang dibeli dan dijual third-party (dikirim langsung dari supplier ke customer)' },
  posts: { en: 'Every goods movement, invoice and payment posts automatically to accounting', id: 'Setiap pergerakan barang, invoice dan pembayaran otomatis terposting ke akuntansi' },
  fi: { en: 'FI — Financial Accounting', id: 'FI — Akuntansi Keuangan' },
  co: { en: 'CO — Controlling', id: 'CO — Controlling' },
  close: { en: 'Month-end close', id: 'Tutup buku bulanan' },
  open: { en: 'Open flow →', id: 'Buka alur →' },
  notUsed: { en: 'Not used in this landscape', id: 'Tidak dipakai di landscape ini' },
  key: { en: 'Key integration points', id: 'Titik integrasi utama' },
  steps: { en: 'steps', id: 'langkah' },
};

const CORE = [
  ['p2p', { en: 'Buy', id: 'Beli' }],
  ['p2m', { en: 'Make', id: 'Produksi' }],
  ['o2c', { en: 'Sell', id: 'Jual' }],
  ['pm', { en: 'Maintain', id: 'Pemeliharaan' }],
];

// What each cycle does to stock and to accounting.
const STOCK = {
  p2p: { en: '↓ Goods receipt into stock (101, released by QM with 321)', id: '↓ Penerimaan barang ke stok (101, dirilis QM dengan 321)' },
  p2m: { en: '↑ Consumes raw material (261) · ↓ output to stock (101)', id: '↑ Pakai bahan baku (261) · ↓ hasil produksi ke stok (101)' },
  o2c: { en: '↑ Stock leaves at post goods issue (601)', id: '↑ Stok keluar saat post goods issue (601)' },
  pm: { en: '↑ Spare parts issued from stock to the order (261)', id: '↑ Spare part dikeluarkan dari stok ke order (261)' },
  blw: { en: 'Customer oil held in our tanks (customer-owned stock)', id: 'Minyak customer di tangki kita (stok milik customer)' },
  pgt: { en: 'Customer oil held in our tanks (customer-owned stock)', id: 'Minyak customer di tangki kita (stok milik customer)' },
  ship: { en: 'No stock — service only', id: 'Tanpa stok — hanya jasa' },
};
const STOCK_KL = { en: 'No stock — goods go supplier → customer', id: 'Tanpa stok — barang dari supplier langsung ke customer' };
const FI = {
  p2p: { en: 'GR/IR, vendor payable (AP), payment', id: 'GR/IR, hutang vendor (AP), pembayaran' },
  p2m: { en: 'Work in process, production variances', id: 'Barang dalam proses, selisih produksi' },
  o2c: { en: 'Cost of goods sold, revenue, receivable (AR)', id: 'HPP, pendapatan, piutang (AR)' },
  pm: { en: 'Maintenance cost to cost center / asset', id: 'Biaya pemeliharaan ke cost center / aset' },
  blw: { en: 'Service revenue, receivable', id: 'Pendapatan jasa, piutang' },
  pgt: { en: 'Service revenue, receivable', id: 'Pendapatan jasa, piutang' },
  ship: { en: 'Service revenue, receivable', id: 'Pendapatan jasa, piutang' },
};

const MD = [
  [{ en: 'Material', id: 'Material' }, 'MM01', ['MM', 'SD', 'PP', 'QM']],
  [{ en: 'Vendor', id: 'Vendor' }, 'XK01', ['MM', 'FI']],
  [{ en: 'Customer', id: 'Customer' }, 'XD01', ['SD', 'FI']],
  [{ en: 'BOM & routing', id: 'BOM & routing' }, 'CS01 · CA01', ['PP', 'CO']],
  [{ en: 'Inspection plan & MIC', id: 'Inspection plan & MIC' }, 'QP01 · QS21', ['QM']],
  [{ en: 'Equipment & maintenance plan', id: 'Equipment & maintenance plan' }, 'IE01 · IP42', ['PM']],
  [{ en: 'Prices & freight rates', id: 'Harga & tarif angkut' }, 'VK11 · ME11', ['SD', 'MM']],
  [{ en: 'GL account, cost center', id: 'Akun GL, cost center' }, 'FS00 · KS01', ['FI', 'CO']],
];

const KEY = [
  { en: 'Goods receipt (MM) posts stock against the GR/IR account (FI); the vendor invoice (MIRO) clears GR/IR and creates the payable.', id: 'Goods receipt (MM) memposting stok lawan akun GR/IR (FI); invoice vendor (MIRO) meng-clear GR/IR dan membentuk hutang.' },
  { en: 'Quality management holds received and produced goods in QI stock until the usage decision releases them.', id: 'Quality management menahan barang yang diterima dan diproduksi di stok QI sampai usage decision melepasnya.' },
  { en: 'Production and maintenance orders collect actual costs and are settled to stock, cost centers or assets (CO).', id: 'Order produksi dan pemeliharaan mengumpulkan biaya aktual lalu disettle ke stok, cost center atau aset (CO).' },
  { en: 'Post goods issue (SD) reduces stock and posts cost of goods sold; billing posts revenue and the receivable, and feeds profitability analysis (CO-PA).', id: 'Post goods issue (SD) mengurangi stok dan memposting HPP; billing memposting pendapatan dan piutang, serta mengisi analisa profitabilitas (CO-PA).' },
  { en: 'At month end the Material Ledger revalues stock and COGS to actual cost, then the FI period is closed.', id: 'Di akhir bulan Material Ledger menilai ulang stok dan HPP ke biaya aktual, lalu periode FI ditutup.' },
];

const Chip = ({ m }) => (
  <span className="inline-block rounded px-1.5 py-px text-[10.5px] font-bold text-white" style={{ background: MODULES[m]?.color || '#64748b' }}>{m}</span>
);

export default function SAPProcessMapOverview({ landKey, land, lang, tr, onOpenFlow }) {
  const L = lang === 'id' ? 'id' : 'en';
  const t = o => (o ? o[L] || o.en : '');
  const hasStock = landKey !== 'kl';
  const flows = land.flows;
  const cycles = [
    ...CORE.map(([k, name]) => ({ k, name, f: flows[k] })),
    ...Object.keys(flows).filter(k => k !== 'close' && !CORE.some(([c]) => c === k)).map(k => ({ k, name: null, f: flows[k] })),
  ];
  const close = flows.close;

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-4 flex flex-col gap-3">
      <div>
        <div className="text-[16px] font-bold text-slate-800">{t(T.title)} · {land.name}</div>
        <p className="text-[12.5px] text-slate-500 m-0">{t(T.sub)}</p>
      </div>

      {/* Master data */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(T.md)}</span>
          <span className="text-[11.5px] text-slate-400">{t(T.mdNote)}</span>
        </div>
        <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {MD.map(([name, tc, mods]) => (
            <div key={name.en} className="bg-white border border-slate-200 rounded-lg px-2 py-1.5">
              <div className="text-[12.5px] font-semibold text-slate-700">{t(name)}</div>
              <div className="flex flex-wrap items-center gap-1 mt-0.5">
                {mods.map(m => <Chip key={m} m={m} />)}
                <span className="font-mono text-[10.5px] text-slate-400 ml-auto">{tc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-center text-slate-300 text-[18px] leading-none">▼</div>

      {/* Business cycles */}
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(T.cycles)}</div>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
        {cycles.map(({ k, name, f }) => {
          if (!f) {
            return (
              <div key={k} className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-slate-400">
                <div className="text-[13.5px] font-bold">{t(name)}</div>
                <div className="text-[12px] mt-1">{t(T.notUsed)}</div>
              </div>
            );
          }
          const lanes = f.lanes || [...new Set(f.steps.map(s => s.lane))];
          return (
            <div key={k} className="rounded-xl border border-slate-200 flex flex-col overflow-hidden">
              <button type="button" onClick={() => onOpenFlow(k)} className="text-left px-3 pt-2.5 pb-2 bg-slate-50 hover:bg-brand-50 border-b border-slate-200">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[13.5px] font-bold text-slate-800">{name ? t(name) + ' · ' : ''}{tr(f.name)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {lanes.map(m => <Chip key={m} m={m} />)}
                  <span className="text-[11px] text-slate-500 ml-1">{tr(f.vol)}</span>
                </div>
              </button>
              <ol className="list-none m-0 p-2 flex flex-col gap-1 flex-1">
                {f.steps.map((s, i) => (
                  <li key={s.id} className="flex items-center gap-2 rounded-md px-2 py-1 text-[12px] text-slate-700"
                    style={{ borderLeft: `3px solid ${MODULES[s.lane]?.color || '#94a3b8'}`, background: '#f8fafc' }}>
                    <span className="font-mono text-[10.5px] text-slate-400 w-4">{i + 1}</span>
                    <span className="flex-1 min-w-0 truncate" title={tr(s.t)}>{tr(s.t)}</span>
                    <span className="text-[10px] font-bold" style={{ color: MODULES[s.lane]?.color }}>{s.lane}</span>
                  </li>
                ))}
              </ol>
              <div className="px-3 py-1.5 text-[11.5px] border-t border-slate-100 text-slate-600 bg-white">
                <div>📦 {t(landKey === 'kl' && k === 'o2c' ? STOCK_KL : STOCK[k]) || '—'}</div>
                <div className="text-slate-500">💰 {t(FI[k]) || '—'}</div>
              </div>
              <button type="button" onClick={() => onOpenFlow(k)} className="text-[11.5px] font-semibold text-brand-700 px-3 py-1.5 text-right hover:bg-brand-50">{t(T.open)}</button>
            </div>
          );
        })}
      </div>

      {/* Inventory hub */}
      <div className="text-center text-slate-300 text-[18px] leading-none">▲▼</div>
      <div className={`rounded-xl px-3 py-2.5 text-[13px] font-semibold text-center ${hasStock ? 'text-white' : 'border border-dashed border-slate-300 text-slate-500'}`}
        style={hasStock ? { background: MODULES.MM.color } : undefined}>
        {t(hasStock ? T.stock : T.noStock)}
      </div>

      {/* FI / CO */}
      <div className="text-center text-[11.5px] text-slate-500">▼ {t(T.posts)} ▼</div>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <div className="rounded-xl px-3 py-2.5 text-white" style={{ background: MODULES.FI.color }}>
          <div className="text-[13.5px] font-bold">{t(T.fi)}</div>
          <div className="text-[12px] opacity-90 mt-1">{L === 'id'
            ? 'Buku besar (GL) · hutang vendor (AP) · piutang customer (AR) · bank · aset tetap · pajak'
            : 'General ledger (GL) · vendor payables (AP) · customer receivables (AR) · bank · fixed assets · tax'}</div>
        </div>
        <div className="rounded-xl px-3 py-2.5 text-white" style={{ background: MODULES.CO.color }}>
          <div className="text-[13.5px] font-bold">{t(T.co)}</div>
          <div className="text-[12px] opacity-90 mt-1">{L === 'id'
            ? 'Cost center · internal/production/maintenance order · product costing & Material Ledger · analisa profitabilitas (CO-PA)'
            : 'Cost centers · internal, production and maintenance orders · product costing & Material Ledger · profitability analysis (CO-PA)'}</div>
        </div>
      </div>
      {close && (
        <button type="button" onClick={() => onOpenFlow('close')} className="text-left rounded-xl border border-slate-200 px-3 py-2.5 hover:bg-brand-50">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(T.close)} · {tr(close.name)}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {close.steps.map((s, i) => (
              <span key={s.id} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-slate-300">→</span>}
                <span className="rounded-md px-2 py-0.5 text-[12px] border" style={{ borderColor: MODULES[s.lane]?.color, color: MODULES[s.lane]?.color }}>
                  {(landKey === 'id' && (s.id === 'mmpv' || s.id === 'ck')) && <span title={lang === 'id' ? 'User / tcode dikunci-dibuka saat langkah ini' : 'Users / tcodes are locked-unlocked around this step'}>🔒 </span>}{tr(s.t)}
                </span>
              </span>
            ))}
          </div>
          {landKey === 'id' && (
            <div className="text-[11.5px] text-amber-700 mt-1.5">🔒 {lang === 'id'
              ? 'Awal bulan: user plant PMI/OSI dikunci (ZBS05) dan MMPV/CKMLCP di-unlock di SM01 — lihat Temuan & catatan → Kalender operasional.'
              : 'Start of month: PMI/OSI plant users locked (ZBS05) and MMPV/CKMLCP unlocked in SM01 — see Findings & notes → Operational calendar.'}</div>
          )}
        </button>
      )}

      {/* Integration points + legend */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(T.key)}</div>
          <ol className="pl-4 m-0 mt-1 text-[12.5px] text-slate-600 space-y-1">{KEY.map(x => <li key={x.en}>{t(x)}</li>)}</ol>
        </div>
        <div className="flex flex-wrap content-start gap-1.5">
          {Object.entries(MODULES).map(([m, v]) => (
            <span key={m} className="flex items-center gap-1 text-[11.5px] text-slate-600 border border-slate-200 rounded-md px-1.5 py-0.5">
              <Chip m={m} /> {v.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
